import { Link } from "@inertiajs/react"
import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { WorkspaceToolbarSearch } from "@/components/workspace/workspace-toolbar-search"
import { useTranslation } from "@/hooks/use-translation"

export type StudioPageChromeRoot = "studio" | "platform" | "company"

export type StudioPageChromeValue = {
  root?: StudioPageChromeRoot
  rootLabel?: ReactNode
  /** When set, root segment is a link (e.g. Wizard → Projects). */
  rootHref?: string
  /** Optional middle crumb (e.g. Dialogs between company and detail title). */
  sectionLabel?: ReactNode
  /** When set, section segment is a link back to the list. */
  sectionHref?: string
  title: ReactNode
}

const StudioPageChromeStateContext = createContext<StudioPageChromeValue | null>(null)
const StudioPageChromeSetContext = createContext<((value: StudioPageChromeValue | null) => void) | null>(null)

export function StudioPageChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<StudioPageChromeValue | null>(null)
  return (
    <StudioPageChromeSetContext.Provider value={setChrome}>
      <StudioPageChromeStateContext.Provider value={chrome}>{children}</StudioPageChromeStateContext.Provider>
    </StudioPageChromeSetContext.Provider>
  )
}

export function useStudioPageChrome(value: StudioPageChromeValue) {
  const setChrome = useContext(StudioPageChromeSetContext)

  useEffect(() => {
    if (!setChrome) return
    setChrome(value)
    // Intentional: sync whenever page chrome fields change; value object is inline per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChrome, value.root, value.rootLabel, value.rootHref, value.sectionLabel, value.sectionHref, value.title])

  useEffect(() => {
    if (!setChrome) return
    return () => setChrome(null)
  }, [setChrome])
}

export function useStudioPageChromeState(): StudioPageChromeValue | null {
  return useContext(StudioPageChromeStateContext)
}

export function StudioToolbarTrail({ chrome }: { chrome: StudioPageChromeValue }) {
  const { t } = useTranslation("layout")
  const resolvedRootLabel =
    chrome.rootLabel ??
    (chrome.root === "platform"
      ? t("shell.sidebar.platform")
      : chrome.root === "company"
        ? t("shell.sidebar.company")
        : chrome.root === "studio"
          ? t("shell.sidebar.studio")
          : null)

  if (!resolvedRootLabel) {
    return <span className="font-medium text-ui-text-primary">{chrome.title}</span>
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      {chrome.rootHref ? (
        <Link href={chrome.rootHref} className="text-ui-text-muted hover:text-ui-text-secondary hover:underline">
          {resolvedRootLabel}
        </Link>
      ) : (
        <span className="text-ui-text-muted">{resolvedRootLabel}</span>
      )}
      {chrome.sectionLabel ? (
        <>
          <span className="text-ui-text-muted">/</span>
          {chrome.sectionHref ? (
            <Link href={chrome.sectionHref} className="text-ui-text-muted hover:text-ui-text-secondary hover:underline">
              {chrome.sectionLabel}
            </Link>
          ) : (
            <span className="text-ui-text-muted">{chrome.sectionLabel}</span>
          )}
        </>
      ) : null}
      <span className="text-ui-text-muted">/</span>
      <span className="font-medium text-ui-text-primary">{chrome.title}</span>
    </span>
  )
}

/** Default shared-toolbar left strip for non-project AppShell pages. */
export function StudioSharedToolbar() {
  const chrome = useStudioPageChromeState()
  return (
    <>
      <WorkspaceToolbarSearch />
      {chrome ? <StudioToolbarTrail chrome={chrome} /> : null}
    </>
  )
}
